import { Component, HostBinding, Input } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { RippleModule } from 'primeng/ripple';
import { MenuItem } from 'primeng/api';
import { LayoutService } from '../service/layout.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-menuitem]',
  imports: [CommonModule, RouterModule, RippleModule],
  template: `
    <ng-container>
      <div
        *ngIf="root && item.visible !== false"
        class="layout-menuitem-root-text"
        [innerHTML]="highlightLabel(item)"
      ></div>
      <a
        *ngIf="(!item.routerLink || item.items) && item.visible !== false"
        [attr.href]="item.url"
        (click)="itemClick($event)"
        [ngClass]="item.styleClass"
        [attr.target]="item.target"
        tabindex="0"
        pRipple
      >
        <div class="grid grid-cols-12 items-center">
          <i [ngClass]="item.icon" class="col-start-1 col-end-2" style="min-width: 2rem;"></i>
          <span class="layout-menuitem-text col-start-3 col-end-11" [innerHTML]="highlightLabel(item)"></span>
          <i class="pi pi-fw pi-angle-down layout-submenu-toggler col-start-12 col-end-13" *ngIf="item.items"></i>
        </div>
      </a>
      <a
        *ngIf="item.routerLink && !item.items && item.visible !== false"
        (click)="itemClick($event)"
        [ngClass]="item.styleClass"
        [ngClass]="{'ruta-activa': url.includes(item.routerLink)}"
        [routerLink]="item.routerLink"
        routerLinkActive="active-route"
        [routerLinkActiveOptions]="
          item.routerLinkActiveOptions || {
            paths: 'exact',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored'
          }
        "
        [fragment]="item.fragment"
        [queryParamsHandling]="item.queryParamsHandling"
        [preserveFragment]="item.preserveFragment"
        [skipLocationChange]="item.skipLocationChange"
        [replaceUrl]="item.replaceUrl"
        [state]="item.state"
        [queryParams]="item.queryParams"
        [attr.target]="item.target"
        tabindex="0"
        pRipple
      >
        <div class="grid grid-cols-12 items-center">
          <i [ngClass]="item.icon" class="col-start-1 col-end-2" style="min-width: 2rem;"></i>
          <span class="layout-menuitem-text col-start-3 col-end-13" [innerHTML]="highlightLabel(item)"></span>
        </div>
      </a>

      <ul *ngIf="item.items && item.visible !== false" [@children]="submenuAnimation">
        <ng-template ngFor let-child let-i="index" [ngForOf]="item.items">
          <li app-menuitem [item]="child" [index]="i" [parentKey]="key" [class]="child['badgeClass']"></li>
        </ng-template>
      </ul>
    </ng-container>
  `,
  animations: [
    trigger('children', [
      state(
        'collapsed',
        style({
          height: '0'
        })
      ),
      state(
        'expanded',
        style({
          height: '*'
        })
      ),
      transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
    ])
  ],
  providers: [LayoutService]
})
export class AppMenuitem {
  @Input() item!: MenuItem;

  @Input() index!: number;

  @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

  @Input() parentKey!: string;

  active = false;

  menuSourceSubscription: Subscription;

  menuResetSubscription: Subscription;

  key: string = '';

  url: string = '';

  constructor(
    public router: Router,
    private layoutService: LayoutService,
    private sanitizer: DomSanitizer
  ) {
    this.menuSourceSubscription = this.layoutService.menuSource$.subscribe((value) => {
      Promise.resolve(null).then(() => {
        if (value.routeEvent) {
          this.active = value.key === this.key || value.key.startsWith(this.key + '-') ? true : false;
        }
        // else {
        //     if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
        //         this.active = false;
        //     }
        // }
      });
    });

    this.menuResetSubscription = this.layoutService.resetSource$.subscribe(() => {
      this.active = false;
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((params) => {
      this.url = this.router.url;
      if (this.item.routerLink) {
        this.updateActiveStateFromRoute();
      }
    });
  }

  ngOnInit() {
    this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);
    this.active = false;

    if (this.item.expanded === true) {
      this.active = true;
    }

    if (this.item['searchActive'] === true) {
      this.active = true;
    }

    if (this.item.routerLink) {      
      this.updateActiveStateFromRoute();
    }
  }

  // Método para resaltar la parte del label que coincide con item.query utilizando el color primario.
  highlightLabel(item: any): SafeHtml {
    let label = item.label;
    const query = item.query;
    if (query && typeof query === 'string') {
      label = this.highlightFlexibleMatch(label, query);
    }
    return this.sanitizer.bypassSecurityTrustHtml(label);
  }

  /**
   * Resalta los caracteres que hacen match con la consulta de manera flexible
   * Solo resalta si hay un match completo (todos los caracteres de la consulta)
   */
  private highlightFlexibleMatch(text: string, query: string): string {
    const normalizedQuery = this.normalizeText(query);
    if (!normalizedQuery) return text;

    const { normalizedText, indexMap } = this.normalizeTextWithIndexMap(text);
    const queryChars = normalizedQuery.split('');

    const matchIndices = this.findBestMatchSequence(normalizedText, queryChars);

    if (!matchIndices) {
      return text;
    }

    const originalMatchIndices = new Set(matchIndices.map((i) => indexMap[i]));

    let highlightedText = '';
    for (let i = 0; i < text.length; i++) {
      if (originalMatchIndices.has(i)) {
        highlightedText += `<span style="color: var(--primary-color); font-weight: bold;">${text[i]}</span>`;
      } else {
        highlightedText += text[i];
      }
    }

    return highlightedText;
  }

  /**
   * Encuentra la subsecuencia de caracteres de la consulta que está más "agrupada"
   * dentro del texto, para un resaltado más natural.
   */
  private findBestMatchSequence(text: string, queryChars: string[]): number[] | null {
    let bestMatch: number[] | null = null;
    let minLength = Infinity;

    for (let i = 0; i <= text.length - queryChars.length; i++) {
      const firstCharMatchIndex = text.indexOf(queryChars[0], i);
      if (firstCharMatchIndex === -1) break;

      const currentMatch = [firstCharMatchIndex];
      let queryIndex = 1;
      let lastMatchIndex = firstCharMatchIndex;

      while (queryIndex < queryChars.length && lastMatchIndex + 1 < text.length) {
        const nextMatchIndex = text.indexOf(queryChars[queryIndex], lastMatchIndex + 1);
        if (nextMatchIndex !== -1) {
          currentMatch.push(nextMatchIndex);
          queryIndex++;
          lastMatchIndex = nextMatchIndex;
        } else {
          break; // No se encontró el siguiente caracter de la consulta
        }
      }

      if (currentMatch.length === queryChars.length) {
        const matchLength = currentMatch[currentMatch.length - 1] - currentMatch[0];
        if (matchLength < minLength) {
          minLength = matchLength;
          bestMatch = currentMatch;
        }
      }
      i = firstCharMatchIndex; // Optimización para el siguiente bucle
    }
    return bestMatch;
  }

  /**
   * Normaliza un texto y devuelve un mapa de índices para poder reconstruir
   * el resaltado en el texto original.
   */
  private normalizeTextWithIndexMap(text: string): { normalizedText: string; indexMap: number[] } {
    let normalizedText = '';
    const indexMap: number[] = [];
    const normalizedOriginal = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    for (let i = 0; i < normalizedOriginal.length; i++) {
      const char = normalizedOriginal[i];
      if (char.trim() !== '') {
        // Solo incluimos caracteres que no sean espacios en blanco
        normalizedText += char.toLowerCase();
        indexMap.push(i);
      }
    }
    return { normalizedText, indexMap };
  }

  /**
   * Normaliza el texto removiendo acentos, espacios y convirtiendo a minúsculas
   */
  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD') // Descompone caracteres acentuados
      .replace(/[\u0300-\u036f]/g, '') // Remueve marcas diacríticas (acentos)
      .replace(/\s+/g, ''); // Remueve todos los espacios
  }

  updateActiveStateFromRoute() {
    let activeRoute = this.router.isActive(this.item.routerLink[0], {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored'
    });

    if (activeRoute) {
      this.layoutService.onMenuStateChange({ key: this.key, routeEvent: true });
    }
  }

  itemClick(event: Event) {
    // avoid processing disabled items
    if (this.item.disabled) {
      event.preventDefault();
      return;
    }

    // execute command
    if (this.item.command) {
      this.item.command({ originalEvent: event, item: this.item });
    }

    // toggle active state
    if (this.item.items) {
      this.active = !this.active;
    }

    this.layoutService.onMenuStateChange({ key: this.key });
  }

  get submenuAnimation() {
    return this.root ? 'expanded' : this.active ? 'expanded' : 'collapsed';
  }

  @HostBinding('class.active-menuitem')
  get activeClass() {
    return this.active && !this.root;
  }

  ngOnDestroy() {
    if (this.menuSourceSubscription) {
      this.menuSourceSubscription.unsubscribe();
    }

    if (this.menuResetSubscription) {
      this.menuResetSubscription.unsubscribe();
    }
  }
}
